import { EditIcon } from '@/assets/icons/edit';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { ErrorText } from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Label from '@/components/basicComponents/label';
import { useErrorMessages } from '@/hooks/i18n';
import { safeJsonParse, safeJsonStringify } from '@/shared/utils/json';
import { jsonEditorTheme, themeVar } from '@/styles/theme';
import { PromptGroupOutputStructureSchemaType } from '@trace-8/trace8-schema';
import { JsonEditor } from 'json-edit-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useFieldEditorContext } from '../../context/FieldEditorContext';
import FieldEditorContentWrapper from '../FieldEditorContentWrapper';

const customIcons = {
  edit: <EditIcon width={24} height={24} fill={themeVar('font.action')} />,
  ok: <ApprovedIcon width={24} height={24} fill={themeVar('context.success')} />,
  cancel: <CloseInfoIcon width={18} height={18} fill={themeVar('context.error')} />
};

type JsonPath = string | number;

interface ArraySchema {
  items?: {
    type: PromptGroupOutputStructureSchemaType;
    properties?: Record<string, any>;
  };
}

interface PropertySchema {
  type: PromptGroupOutputStructureSchemaType;
  properties?: Record<string, any>;
}

const JsonContent = () => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { currentField, handleUpdateFieldAnswer, mainQueryRefetch, handleClose } = useFieldEditorContext();
  const { control, reset, setValue, handleSubmit } = useForm<{ fieldValue: string | JSON }>({ defaultValues: { fieldValue: '' } });

  const [isEditMode, setIsEditMode] = useState(true);
  const [outputStructure, setOutputStructure] = useState<any | null>(null);
  const [defaultOutputStructure, setDefaultOutputStructure] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!currentField) return;
    const { value, AI, promptGroup } = currentField;

    if (promptGroup?.outputStructure) {
      setOutputStructure(safeJsonParse(promptGroup.outputStructure));
    }

    if (value === null && AI?.answerJson) {
      setIsEditMode(false);

      const parsedAiSuggestionValue = safeJsonParse(AI.answerJson) || '';
      reset({ fieldValue: parsedAiSuggestionValue });
    } else {
      setIsEditMode(true);

      const defaultOutputStructure = generateDefaultStructureFromSchema(promptGroup?.outputStructure);
      setDefaultOutputStructure(defaultOutputStructure || null);
      const parsedValue = safeJsonParse(value) || defaultOutputStructure;
      reset({ fieldValue: parsedValue });
    }
  }, [currentField]);

  const saveValue = async (data: { fieldValue: string | JSON }) => {
    try {
      setError(null);
      setLoading(true);

      const { fieldValue } = data;

      const stringifiedFieldValue = (typeof fieldValue === 'string' ? data.fieldValue : safeJsonStringify(data.fieldValue)) as string;

      await handleUpdateFieldAnswer({
        id: currentField.fieldAnswerId,
        recordId: currentField.recordId,
        value: isEditMode ? stringifiedFieldValue : null
      });

      await mainQueryRefetch();
      handleClose();
    } catch (error) {
      setError({ title: 'Error', message: defaultErrorMessage });
    } finally {
      setLoading(false);
    }
  };

  const editSuggested = (suggestion: string) => {
    const parsedAiSuggestionValue = safeJsonParse(currentField?.AI?.answerJson) || '';
    setValue('fieldValue', parsedAiSuggestionValue);
    setIsEditMode(true);
  };

  // const clearSuggestion = () => {
  //   setValue('fieldValue', currentField?.AI?.answerJson ? '' : null);
  //   setIsEditMode(true);
  // };

  const startWithAISuggestion = () => {
    const parsedAiSuggestionValue = safeJsonParse(currentField?.AI?.answerJson) || '';
    setValue('fieldValue', parsedAiSuggestionValue);
    setIsEditMode(false);
  };

  const revertPriorDraft = () => {
    if (currentField?.value) {
      const parsedValue = safeJsonParse(currentField.value) || currentField?.value || '';
      setValue('fieldValue', parsedValue);
      setIsEditMode(true);
    }
  };

  const generateDefaultStructureFromSchema = (outputStructure: string | null): any => {
    const parsedStructure = safeJsonParse(outputStructure);
    if (!parsedStructure?.jsonFormat) return '';

    const { jsonFormat } = parsedStructure;

    switch (jsonFormat.type) {
      case PromptGroupOutputStructureSchemaType.ARRAY:
        return [createEmptyObjectFromSchema(jsonFormat)];
      case PromptGroupOutputStructureSchemaType.OBJECT:
        return createEmptyObjectFromSchema(jsonFormat);
      default:
        return '';
    }
  };

  const getDefaultValueForPropertyType = (property: PropertySchema): any => {
    const { type } = property;

    const typeDefaultValues: Record<string, any> = {
      [PromptGroupOutputStructureSchemaType.STRING]: '',
      [PromptGroupOutputStructureSchemaType.NUMBER]: 0,
      [PromptGroupOutputStructureSchemaType.INTEGER]: 0,
      [PromptGroupOutputStructureSchemaType.BOOLEAN]: false,
      [PromptGroupOutputStructureSchemaType.ARRAY]: [createEmptyObjectFromSchema(property)]
    };

    const defaultValue = typeDefaultValues[type];
    return defaultValue !== undefined ? defaultValue : '';
  };

  const createEmptyObjectFromSchema = (jsonFormat: any, path?: JsonPath[]): any => {
    if (path?.length > 0 && defaultOutputStructure) {
      if (typeof defaultOutputStructure === 'object' && !Array.isArray(defaultOutputStructure)) {
        const nestedStructure = defaultOutputStructure?.[path[0]];
        if (nestedStructure) {
          return nestedStructure;
        }
      }
    }

    if (!jsonFormat) return null;

    switch (jsonFormat.type) {
      case PromptGroupOutputStructureSchemaType.ARRAY:
        return createArrayItemFromSchema(jsonFormat);
      case PromptGroupOutputStructureSchemaType.OBJECT:
        return createObjectFromSchema(jsonFormat);
      default:
        return null;
    }
  };

  const createArrayItemFromSchema = (arraySchema: ArraySchema): any => {
    if (!arraySchema?.items?.type) {
      return null;
    }

    const defaultValue = getDefaultValueForPropertyType(arraySchema.items);

    if (arraySchema.items.type === PromptGroupOutputStructureSchemaType.OBJECT) {
      if (!arraySchema.items?.properties) return null;
      return Object.fromEntries(
        Object.entries(arraySchema.items.properties).map(([key, property]) => [key, getDefaultValueForPropertyType(property as PropertySchema)])
      );
    }

    return defaultValue;
  };

  const createObjectFromSchema = (objectSchema: any): any => {
    if (!objectSchema.properties) return null;

    return Object.fromEntries(
      Object.entries(objectSchema.properties).map(([key, property]) => [key, getDefaultValueForPropertyType(property as PropertySchema)])
    );
  };

  if (!currentField) return <></>;

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <FieldEditorContentWrapper
        isEditMode={isEditMode}
        showRevertPriorDraft={false}
        error={error}
        confirmButton={{ name: 'Save', onClick: handleSubmit(saveValue) }}
        startWithAISuggestion={startWithAISuggestion}
        editSuggested={editSuggested}
        revertPriorDraft={revertPriorDraft}
      >
        <div>
          <Label size="md">{currentField.name}</Label>
          <JsonContentWrapper isEditMode={isEditMode}>
            {!isEditMode && (
              <AiIconWrapper>
                <AiAssistantIcon width={28} height={28} fill={themeVar('font.base')} />
              </AiIconWrapper>
            )}
            <JsonEditorWrapper isEditMode={isEditMode}>
              <Controller
                name="fieldValue"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <>
                    <JsonEditor
                      data={value}
                      setData={onChange}
                      maxWidth="100%"
                      viewOnly={!isEditMode}
                      theme={jsonEditorTheme}
                      icons={customIcons}
                      enableClipboard={false}
                      onAdd={({ currentData, path }) => {
                        // Handle root level array additions
                        if (Array.isArray(currentData) && outputStructure) {
                          const newItem = createEmptyObjectFromSchema(outputStructure?.jsonFormat);
                          if (newItem !== null && newItem !== undefined) {
                            return ['value', [...currentData, newItem]];
                          }
                        }

                        // Handle nested array additions
                        if (path && path.length > 0 && currentData && typeof currentData === 'object') {
                          const nestedArray = (currentData as any)[path?.[0]];
                          if (Array.isArray(nestedArray)) {
                            const newNestedItem = createEmptyObjectFromSchema(outputStructure?.jsonFormat, path as JsonPath[]);
                            if (newNestedItem !== null && newNestedItem !== undefined) {
                              const updatedNestedArray = [...nestedArray, ...newNestedItem];
                              return ['value', { ...currentData, [path[0]]: updatedNestedArray }];
                            }
                          }
                        }
                      }}
                      restrictAdd={({ value }) => {
                        if (Array.isArray(value)) return false;
                        return true;
                      }}
                      restrictDelete={({ value, parentData }) => {
                        if (Array.isArray(parentData) && value !== null) return false;
                        return true;
                      }}
                      restrictTypeSelection={true}
                      restrictEdit={({ value }) => {
                        if (typeof value === 'object' && value !== null) return true;
                        return false;
                      }}
                      rootName=""
                      TextEditor={() => null}
                    />
                    {error?.message && <ErrorText size="md">{error?.message}</ErrorText>}
                  </>
                )}
              />
            </JsonEditorWrapper>
          </JsonContentWrapper>
        </div>
      </FieldEditorContentWrapper>
    </>
  );
};

export default JsonContent;

const JsonContentWrapper = styled.div<{ isEditMode: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ isEditMode }) => (isEditMode ? themeVar('layer.1') : themeVar('action.disabled'))};
`;

const AiIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const JsonEditorWrapper = styled.div<{ isEditMode: boolean }>`
  padding-left: 10px;
  border-left: ${({ isEditMode }) => (isEditMode ? 'none' : `1px solid ${themeVar('border.base')}`)};

  .jer-confirm-buttons {
    margin: 0 0.4em;
  }
  .jer-select {
    background-color: ${themeVar('layer.1')};
    border: 1px solid ${themeVar('border.base')};
    padding: 8px 5px;
    border-radius: 3px;
  }
  .jer-select-inner {
    font-size: 16px;
    line-height: 18.2px;
  }
`;
